import { useMemo } from 'react';
import { motion } from 'framer-motion';

interface FanLevelProgressProps {
  accentColor: string;
  fanLevel: number;
  currentExp: number;
  nextLevelExp: number;
  checkInExp: number;
  supportExp: number;
  hasVeteranBonus: boolean;
  onResonateClick?: () => void;
  compact?: boolean;
}

interface LevelBenefit {
  level: number;
  label: string;
  icon: string;
  benefits: { icon: string; text: string }[];
}

const LEVEL_BENEFITS: LevelBenefit[] = [
  {
    level: 0,
    label: 'NEWCOMER',
    icon: 'ri-user-line',
    benefits: [
      { icon: 'ri-eye-line', text: 'アーティストページ閲覧' },
      { icon: 'ri-heart-line', text: 'お気に入り登録' },
      { icon: 'ri-sparkling-2-line', text: 'RESONATE（投げ銭）' },
    ],
  },
  {
    level: 1,
    label: 'FOLLOWER',
    icon: 'ri-seedling-line',
    benefits: [
      { icon: 'ri-chat-3-line', text: 'LV.1限定 ECHOES 閲覧' },
      { icon: 'ri-star-line', text: 'ファンバッジ表示' },
      { icon: 'ri-notification-line', text: '新着通知を受け取る' },
    ],
  },
  {
    level: 2,
    label: 'DEEP FAN',
    icon: 'ri-fire-line',
    benefits: [
      { icon: 'ri-lock-unlock-line', text: 'LV.2限定 ECHOES 閲覧' },
      { icon: 'ri-vip-line', text: 'DEEP FAN バッジ' },
      { icon: 'ri-gift-line', text: '限定コンテンツへのアクセス' },
    ],
  },
  {
    level: 3,
    label: 'CORE',
    icon: 'ri-vip-crown-line',
    benefits: [
      { icon: 'ri-lock-unlock-line', text: 'LV.3限定 ECHOES 閲覧' },
      { icon: 'ri-award-line', text: 'CORE ファン認定バッジ' },
      { icon: 'ri-vip-diamond-line', text: 'クレジット掲載（累計¥10,000+）' },
    ],
  },
];

function getLevelColor(level: number, accentColor: string): string {
  switch (level) {
    case 0: return 'rgba(156,163,175,0.8)';
    case 1: return '#4ade80';
    case 2: return '#fb923c';
    case 3: return '#f5c842';
    default: return accentColor;
  }
}

function getLevelGlow(level: number): string {
  switch (level) {
    case 0: return 'rgba(156,163,175,0.3)';
    case 1: return 'rgba(74,222,128,0.4)';
    case 2: return 'rgba(251,146,60,0.4)';
    case 3: return 'rgba(245,200,66,0.5)';
    default: return 'rgba(255,255,255,0.2)';
  }
}

export function FanLevelProgress({
  accentColor,
  fanLevel,
  currentExp,
  nextLevelExp,
  checkInExp,
  supportExp,
  hasVeteranBonus,
  onResonateClick,
  compact = false,
}: FanLevelProgressProps) {
  const progressPercent = useMemo(
    () => Math.min(100, Math.round((currentExp / nextLevelExp) * 100)),
    [currentExp, nextLevelExp]
  );

  const nextLevel = Math.min(fanLevel + 1, 3);
  const isMaxLevel = fanLevel >= 3;

  return (
    <div className={compact ? 'space-y-6' : 'space-y-8'}>
      {/* セクションタイトル */}
      <div className="text-center">
        <div
          className="inline-flex items-center gap-3 px-6 py-3 rounded-full border-2"
          style={{
            borderColor: `${accentColor}40`,
            background: `linear-gradient(135deg, ${accentColor}15, transparent)`,
            boxShadow: `0 0 30px ${accentColor}20`,
          }}
        >
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
          />
          <h3
            className="text-xl font-black tracking-[0.3em] uppercase"
            style={{ color: accentColor }}
          >
            FAN DEPTH
          </h3>
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
          />
        </div>
        <p className="text-gray-600 text-xs tracking-[0.2em] mt-3 uppercase">ファンレベル進捗</p>
      </div>

      {/* 現在のレベルカード */}
      <div
        className="relative rounded-2xl p-6 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${getLevelColor(fanLevel, accentColor)}12, ${getLevelColor(fanLevel, accentColor)}05)`,
          border: `2px solid ${getLevelColor(fanLevel, accentColor)}50`,
          boxShadow: `0 0 40px ${getLevelGlow(fanLevel)}, inset 0 0 30px ${getLevelColor(fanLevel, accentColor)}08`,
        }}
      >
        {/* 背景グロー */}
        <div
          className="absolute inset-0 pointer-events-none opacity-10"
          style={{
            background: `radial-gradient(circle at 30% 50%, ${getLevelColor(fanLevel, accentColor)}60, transparent 60%)`,
          }}
        />

        <div className="relative flex items-start gap-5">
          {/* レベルアイコン */}
          <div
            className="flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center border-2"
            style={{
              background: `${getLevelColor(fanLevel, accentColor)}18`,
              borderColor: `${getLevelColor(fanLevel, accentColor)}60`,
              boxShadow: `0 0 20px ${getLevelGlow(fanLevel)}`,
            }}
          >
            <i
              className={`${LEVEL_BENEFITS[Math.min(fanLevel, 3)].icon} text-2xl`}
              style={{ color: getLevelColor(fanLevel, accentColor) }}
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <span
                className="text-xs font-black tracking-[0.2em] uppercase"
                style={{ color: getLevelColor(fanLevel, accentColor) }}
              >
                {LEVEL_BENEFITS[Math.min(fanLevel, 3)].label}
              </span>
              <span
                className="px-2.5 py-0.5 rounded-full text-xs font-black border"
                style={{
                  background: `${getLevelColor(fanLevel, accentColor)}18`,
                  borderColor: `${getLevelColor(fanLevel, accentColor)}50`,
                  color: getLevelColor(fanLevel, accentColor),
                }}
              >
                LV. {fanLevel}
              </span>
            </div>

            {/* EXP表示 */}
            <div className="flex items-baseline gap-2 mb-3">
              <span
                className="font-mono text-3xl font-black"
                style={{
                  color: getLevelColor(fanLevel, accentColor),
                  textShadow: `0 0 20px ${getLevelGlow(fanLevel)}`,
                }}
              >
                {currentExp}
              </span>
              <span className="text-gray-500 text-sm font-mono">
                / {isMaxLevel ? '∞' : nextLevelExp} EXP
              </span>
            </div>

            {/* プログレスバー */}
            {!isMaxLevel && (
              <div className="mb-2">
                <div
                  className="h-2.5 rounded-full overflow-hidden"
                  style={{ background: `${getLevelColor(fanLevel, accentColor)}18` }}
                >
                  <motion.div
                    className="h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 1, ease: 'easeOut', delay: 0.3 }}
                    style={{
                      background: `linear-gradient(90deg, ${getLevelColor(fanLevel, accentColor)}, ${getLevelColor(fanLevel, accentColor)}aa)`,
                      boxShadow: `0 0 12px ${getLevelGlow(fanLevel)}`,
                    }}
                  />
                </div>
                <div className="flex justify-between mt-1.5">
                  <span className="text-gray-600 text-xs font-mono">{progressPercent}%</span>
                  <span className="text-gray-600 text-xs font-mono">
                    あと {nextLevelExp - currentExp} EXP で LV.{nextLevel}
                  </span>
                </div>
              </div>
            )}

            {isMaxLevel && (
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-black"
                style={{
                  background: `${getLevelColor(3, accentColor)}18`,
                  border: `1px solid ${getLevelColor(3, accentColor)}50`,
                  color: getLevelColor(3, accentColor),
                }}
              >
                <i className="ri-vip-crown-fill" />
                MAX LEVEL REACHED
              </div>
            )}
          </div>
        </div>

        {/* EXP内訳 */}
        <div
          className="mt-5 pt-4 grid grid-cols-3 gap-3"
          style={{ borderTop: `1px solid ${getLevelColor(fanLevel, accentColor)}20` }}
        >
          {[
            {
              icon: 'ri-map-pin-line',
              label: 'チェックイン',
              value: `${checkInExp} EXP`,
              sub: `${Math.floor(checkInExp / 5)}回`,
            },
            {
              icon: 'ri-sparkling-2-line',
              label: '直接支援',
              value: `${supportExp} EXP`,
              sub: `¥${(supportExp * 1000).toLocaleString()}`,
            },
            {
              icon: 'ri-time-line',
              label: '古参ボーナス',
              value: hasVeteranBonus ? 'あり' : 'なし',
              sub: hasVeteranBonus ? '+1 EXP' : '—',
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl p-3 text-center"
              style={{
                background: `${getLevelColor(fanLevel, accentColor)}08`,
                border: `1px solid ${getLevelColor(fanLevel, accentColor)}20`,
              }}
            >
              <i
                className={`${item.icon} text-base mb-1`}
                style={{ color: getLevelColor(fanLevel, accentColor) }}
              />
              <p className="text-gray-500 text-[10px] tracking-wider mb-0.5">{item.label}</p>
              <p
                className="font-mono text-sm font-bold"
                style={{ color: getLevelColor(fanLevel, accentColor) }}
              >
                {item.value}
              </p>
              <p className="text-gray-600 text-[10px] font-mono">{item.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* レベルロードマップ */}
      <div className="space-y-3">
        <p className="text-gray-600 text-xs tracking-[0.2em] uppercase text-center">LEVEL ROADMAP</p>

        <div className="relative">
          {/* 縦ライン */}
          <div
            className="absolute left-7 top-8 bottom-8 w-px"
            style={{
              background: `linear-gradient(to bottom, ${getLevelColor(0, accentColor)}40, ${getLevelColor(3, accentColor)}40)`,
            }}
          />

          <div className="space-y-3">
            {LEVEL_BENEFITS.map((levelInfo) => {
              const isUnlocked = fanLevel >= levelInfo.level;
              const isCurrent = fanLevel === levelInfo.level;
              const lColor = getLevelColor(levelInfo.level, accentColor);

              return (
                <motion.div
                  key={levelInfo.level}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: levelInfo.level * 0.1 }}
                  className="relative flex gap-4"
                >
                  {/* レベルアイコン（左） */}
                  <div
                    className="relative flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center border-2 z-10"
                    style={
                      isUnlocked
                        ? {
                            background: `${lColor}18`,
                            borderColor: `${lColor}70`,
                            boxShadow: isCurrent
                              ? `0 0 20px ${getLevelGlow(levelInfo.level)}, 0 0 40px ${getLevelGlow(levelInfo.level)}`
                              : `0 0 12px ${getLevelGlow(levelInfo.level)}`,
                          }
                        : {
                            background: 'rgba(255,255,255,0.03)',
                            borderColor: 'rgba(255,255,255,0.08)',
                          }
                    }
                  >
                    <i
                      className={`${levelInfo.icon} text-xl`}
                      style={{ color: isUnlocked ? lColor : 'rgba(75,85,99,0.6)' }}
                    />
                    {/* 現在レベルのパルスリング */}
                    {isCurrent && (
                      <div
                        className="absolute inset-0 rounded-xl animate-ping opacity-30"
                        style={{ border: `2px solid ${lColor}` }}
                      />
                    )}
                  </div>

                  {/* コンテンツ */}
                  <div
                    className="flex-1 rounded-xl p-4"
                    style={
                      isUnlocked
                        ? {
                            background: `linear-gradient(135deg, ${lColor}10, ${lColor}04)`,
                            border: `1px solid ${lColor}${isCurrent ? '50' : '25'}`,
                            boxShadow: isCurrent ? `0 0 20px ${getLevelGlow(levelInfo.level)}` : 'none',
                          }
                        : {
                            background: 'rgba(255,255,255,0.02)',
                            border: '1px solid rgba(255,255,255,0.06)',
                          }
                    }
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="font-mono text-xs font-black tracking-widest"
                        style={{ color: isUnlocked ? lColor : 'rgba(75,85,99,0.6)' }}
                      >
                        LV.{levelInfo.level}
                      </span>
                      <span
                        className="text-xs font-bold tracking-wider"
                        style={{ color: isUnlocked ? lColor : 'rgba(75,85,99,0.5)' }}
                      >
                        {levelInfo.label}
                      </span>
                      {isCurrent && (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider"
                          style={{
                            background: `${lColor}22`,
                            border: `1px solid ${lColor}60`,
                            color: lColor,
                          }}
                        >
                          NOW
                        </span>
                      )}
                      {!isUnlocked && (
                        <span className="flex items-center gap-1 text-[10px] text-gray-600 tracking-wider">
                          <i className="ri-lock-line text-xs" />
                          LOCKED
                        </span>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      {levelInfo.benefits.map((benefit, bi) => (
                        <div key={bi} className="flex items-center gap-2">
                          <i
                            className={`${benefit.icon} text-xs flex-shrink-0`}
                            style={{ color: isUnlocked ? lColor : 'rgba(75,85,99,0.4)' }}
                          />
                          <span
                            className="text-xs tracking-wide"
                            style={{
                              color: isUnlocked ? 'rgba(209,213,219,0.9)' : 'rgba(75,85,99,0.5)',
                            }}
                          >
                            {benefit.text}
                          </span>
                          {isUnlocked && (
                            <i
                              className="ri-check-line text-xs ml-auto flex-shrink-0"
                              style={{ color: lColor }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* レベルアップCTA */}
      {!isMaxLevel && (
        <div
          className="rounded-2xl p-5 text-center"
          style={{
            background: `linear-gradient(135deg, ${accentColor}10, ${accentColor}04)`,
            border: `1px solid ${accentColor}30`,
            boxShadow: `0 0 20px ${accentColor}15`,
          }}
        >
          <p className="text-gray-400 text-sm mb-1">
            <span className="font-bold" style={{ color: accentColor }}>LV.{nextLevel}</span> に上がるには？
          </p>
          <p className="text-gray-600 text-xs mb-4 tracking-wide">
            ライブに行く・投げ銭する・グッズを買う
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { icon: 'ri-calendar-event-line', label: 'ライブに行く', sub: '+5 EXP/回' },
              { icon: 'ri-sparkling-2-line', label: '投げ銭する', sub: '+1 EXP/¥1,000' },
              { icon: 'ri-shopping-bag-line', label: 'グッズを買う', sub: '+EXP' },
            ].map((btn) => (
              <button
                key={btn.label}
                onClick={btn.label === '投げ銭する' ? onResonateClick : undefined}
                className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl text-xs font-bold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                style={{
                  background: `${accentColor}10`,
                  border: `1px solid ${accentColor}35`,
                  color: accentColor,
                }}
              >
                <i className={`${btn.icon} text-base`} />
                <span>{btn.label}</span>
                <span
                  className="text-[10px] font-mono opacity-70"
                  style={{ color: accentColor }}
                >
                  {btn.sub}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
